/**
 * Confronto fra nomi di variazione ("Mensile", "Annuale", ...) lato client.
 *
 * Stessa regola di `util/VariationNames` lato server, e per lo stesso motivo: nei dati reali lo
 * stesso nome convive scritto in modi diversi. Misurato in produzione su `products`: accanto a
 * "Annuale" (10) esiste "Annuale " con lo spazio finale (6), e accanto a "14 giorni" (16) esiste
 * "14 Giorni" (2). Le due differenze sono su assi diversi — spazi e maiuscole — quindi normalizzare
 * solo con trim ne coprirebbe una sola.
 *
 * Un confronto letterale fallisce su una parte delle variazioni e non su tutte, che è il modo
 * peggiore di sbagliare: sembra funzionare finché non capita il prodotto sbagliato.
 */

/** Forma comparabile: spazi ai margini rimossi, differenze di maiuscole ignorate. */
export function normalizeVariationName(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : null;
}

/** I due nomi indicano la stessa variazione. */
export function variationNamesMatch(a, b) {
  const na = normalizeVariationName(a);
  const nb = normalizeVariationName(b);
  return na !== null && nb !== null && na === nb;
}

/**
 * Risolve un nome di variazione — tipicamente arrivato da un parametro URL — contro le variazioni
 * reali del prodotto, restituendo **l'oggetto del catalogo** o `null`.
 *
 * Restituire l'oggetto e non la stringa ricevuta è il punto: da qui in poi il codice lavora sul
 * nome canonico del catalogo, quindi i confronti successivi possono restare letterali senza
 * riaprire il problema.
 *
 * `null` copre ogni forma di valore inutilizzabile — assente, vuoto, non stringa (un array, come
 * in `?variationName=a&variationName=b`, o un oggetto), o semplicemente non corrispondente a
 * nessuna variazione. Chi chiama deve trattare tutti questi casi allo stesso modo: **come se il
 * parametro non fosse stato passato**, senza mostrare errori a chi sta pagando.
 */
export function resolveVariationFromCatalog(variations, rawName) {
  const wanted = normalizeVariationName(rawName);
  if (wanted === null || wanted === '') return null;
  if (!Array.isArray(variations)) return null;
  return variations.find((v) => normalizeVariationName(v?.variationName) === wanted) ?? null;
}
